import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { Validators, FormGroup, FormArray, FormControl } from "@angular/forms";
import { Store } from "@ngrx/store";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogConfig, MatDialogModule } from "@angular/material/dialog";
import { ApplyPipeModule, UiPromiseService } from "ngx-zcs-components";
import {
  ConnextData,
  DeleteDeviceInCertifiedPlantMutation,
  deviceSerialValidator,
  DeviceType,
  GenericDeviceValidator,
  InverterType,
  isNotNullOrUndefined,
  PlantDeviceInput,
  ProductModelService,
  StateService,
  System,
  Systems,
  UpdateDeviceInCertifiedPlantMutation,
} from "ngx-core-services";
import gql from "graphql-tag";

import { IReduxBlock } from "../../store/helpers/decorator";
import { selectedSystem, SystemDefinition, SystemsState } from "../../store";
import { DeviceListModule } from "../device-list/device-list.module";
import { HeaderOptionsModule } from "../components/header-options/header-options.module";
import { ConnextDevicesSectionComponent } from "../connext-devices-section/connext-devices-section.component";
import { RemoveInverterFromPlant, VirtualDevicesSectionComponent } from "../virtual-devices-section/virtual-devices-section.component";
import { AzzurroHubDevicesSectionComponent } from "../azzurro-hub-devices-section/azzurro-hub-devices-section.component";
import { inverterListValidator, serialUniqueValidator } from "../create-firebase/create.utils";
import { convertProductTypeNameToPlantDeviceType } from "../create-aws/create-aws.utils";

const VIRTUAL_DEVICE_TYPES: DeviceType[] = ['inverter', 'meter', 'enerclic'];

const getLabels = () => ({
  actions: 'Actions',
  addInverter: 'Add',
  addRemove: 'Add/Remove',
  applyRegistration: 'Apply registration',
  certifiedPlantsInverters: 'Certified plant\'s inverters',
  certifiedPlantsInvertersDescription: 'This list of inverters belong to the composition of your certified system. If you want to remove them, click on the "Restore" button.',
  connextsInverters: 'Connext\'s inverters',
  deviceAlreadyOnACertifiedPlant: 'Device already on a certified plant',
  deviceNotExist: 'Device does not exist',
  error: 'Error',
  evChCharging: 'Charging',
  evChError: 'Error',
  evChIdle: 'Idle',
  evChParking: 'Parking',
  evChUnknown: 'Unknown',
  inverterListDescription: 'Enter the list of your devices specifying the serial numbers and the Modbus address. You can find the device serial number on the adhesive label. For example, for inverters, the label is located on the side.',
  inverterListPlaceholder: 'E.g. ZM2',
  inverterListTitle: 'Inverter List',
  inverterModbusDescription: 'Enter the Modbus address already assigned to the inverter',
  inverterModbusDescriptionInnerCard: 'Make sure to enter the Modbus (RS485) address configured on the inverter. If you have any doubts, contact the installer.',
  inverterModbusPlaceholder: 'E.g. 1',
  inverterRegistrationError: 'Error registering the inverter',
  inverters: 'Inverters',
  inverterVirtualListDescription: 'Enter the list of your devices specifying the serial numbers. You can find the device serial number on the adhesive label. For example, for inverters, it is located on the side.',
  leastOneDevice: 'At least one device is required',
  logger: 'Logger',
  modifyPlant: 'Modify plant',
  multiplicativeConstant: 'Multiplicative constant',
  noActions: 'No actions',
  noDevice: 'No device',
  productionSensor: 'Production Sensor',
  productionSensorDesc: 'The reading of this sensor will be used to calculate the power production',
  productionSensorDescWithConstMultiplicative: 'Set the multiplicative constant, it will be used in the calculation of the produced power',
  productionSensorPlaceholder: 'E.g. 3',
  registered: 'Registered',
  restore: 'Restore',
  restoreDevicesAndSensors: 'Restore devices and sensors',
  restoreDevicesAndSensorsDescription: 'This action will remove all added devices and sensors and restore the default configuration.',
  save: 'Save',
  sensorRegistrationError: 'Error registering the sensor',
  sensors: 'Sensors',
  serial: 'Serial',
  serialNotValid: 'Serial not valid',
  setup: 'Settings',
  status: 'Status',
  statusConnected: 'Connected',
  statusDisconnected: 'Disconnected',
  statusNotUpdated: 'Not updated',
  statusUninstalled: 'Uninstalled',
  title: 'Device list',
  type: 'Type',
  uniqueSerial: 'The serial number must be unique',
  updatingSystemConfiguration: 'Updating system configuration',
  validationError: 'Validation error',
  atLeastOneActiveInverter: 'At least one active inverter must be present',
  thereAreUnsavedChanges: 'There are unsaved changes',
  attention: 'Attention',
  uninstall: 'Uninstall',
  uninstalled: 'Uninstalled',
  statusUpdated: 'Updated',
  delete: 'Delete',
  active: 'Active',
  addDevice: 'Add Device',
  addedDevice: 'Added Device',
  detectedDevices: 'Devices Detected by Hub',
  detectedDevicesDescription: 'These devices were automatically detected by your Azzurro Hub.',
  manualDeviceList: 'Manual Device Entry',
  manualDeviceListDescription: 'Manually enter device serial numbers.',
  manualDeviceListPlaceholder: 'E.g. ZM24D12345678',
  noDetectedDevices: 'No devices detected',
  noAddedDevices: 'No devices added',
  noAddedDevicesDescription: 'No devices have been added to this plant yet. Only inverters, meters, and Power Magic devices appear in this list. EMS devices are not shown here.',
  advanced: 'Advanced',
  advancedDescription: 'Manual device entry and advanced options',
  azzurroHubOffline: 'Azzurro Hub offline',
  devicesAddedToAdvancedPlant: 'Devices Added to Advanced Plant',
  clickToAddToYourPlant: 'Click to add them to your plant',
  scanning: 'Scanning...',
  listFoundDevices: 'List Found Devices',
  startRescan: 'Start Re-scan',
  refreshDevices: 'Refresh',
  deviceListContent: 'Device List',
});

@Component({
  selector: 'lib-devices-block',
  standalone: true,
  imports: [
    ApplyPipeModule,
    AzzurroHubDevicesSectionComponent,
    CommonModule,
    ConnextDevicesSectionComponent,
    DeviceListModule,
    HeaderOptionsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    VirtualDevicesSectionComponent,
  ],
  templateUrl: './devices-block.component.html',
  styleUrls: ['./devices-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesBlockComponent implements IReduxBlock {
  @ViewChild('connextDevices') connextDevices?: TemplateRef<any>;
  @ViewChild('virtualDevices') virtualDevices?: TemplateRef<any>;
  @ViewChild('azzurroHubDevices') azzurroHubDevices?: TemplateRef<any>;

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  @Input() systems$?: Observable<Systems | undefined>;

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _instanceId$: Observable<string> = new Observable<string>();

  @Input() compareMode = false;
  @Input() selectedDevicesForComparison: string[] = [];

  @Output() goToDevicePage = new EventEmitter<string>();
  @Output() compare = new EventEmitter<string>();
  @Output() inverterCountChange = new EventEmitter<number>();

  readonly selectedSystemDefinition$ = this.store.select(selectedSystem).pipe(isNotNullOrUndefined());

  readonly inverterSyncValidators = [
    Validators.required,
    deviceSerialValidator(VIRTUAL_DEVICE_TYPES),
    serialUniqueValidator,
  ];

  readonly inverterAsyncValidators = (system?: SystemDefinition) => (system?.source === 'aws' ? [
    this.genericDeviceValidator.deviceTypeExists(VIRTUAL_DEVICE_TYPES),
    this.genericDeviceValidator.checkAvailableDeviceWithCertifiedPlant(system.uid ?? ''),
  ]: [
    this.genericDeviceValidator.deviceTypeExists(VIRTUAL_DEVICE_TYPES),
  ]);

  readonly connextInverterSyncValidators = [
    Validators.required,
    deviceSerialValidator(['inverter']),
    serialUniqueValidator,
  ];

  readonly connextInverterAsyncValidators = (system?: SystemDefinition) => (system?.source === 'aws' ? [
    this.genericDeviceValidator.checkAvailableDeviceWithCertifiedPlant(system.uid ?? ''),
  ] : []);

  readonly azzurroHubDeviceSyncValidators = [
    Validators.required,
    deviceSerialValidator(['inverter', 'meter']),
    serialUniqueValidator,
  ];

  virtualDevicesConfig = new FormGroup({
    inverterList: new FormArray([
      new FormGroup({
        inverterSerial: new FormControl('', this.inverterSyncValidators, this.inverterAsyncValidators()),
        added: new FormControl(false, Validators.requiredTrue),
        dismiss: new FormControl(null),
      }),
    ]),
  }, {
    validators: inverterListValidator,
  });

  connextDevicesConfig = new FormGroup({
    productionSensor: new FormGroup({
      productionSensorK: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(10),
      ]),
      id: new FormControl('', Validators.required),
      registered: new FormControl(false),
    }),
    inverterList: new FormArray([
      new FormGroup({
        inverterSerial: new FormControl('', this.connextInverterSyncValidators, this.connextInverterAsyncValidators()),
        inverterModbusId: new FormControl('', [
          Validators.required,
          Validators.min(1),
          Validators.max(255),
          Validators.pattern('^[0-9]*$'),
        ]),
        setup: new FormControl(false),
        registered: new FormControl(false),
      }),
    ]),
  });

  azzurroHubDevicesConfig = new FormGroup({
    deviceList: new FormArray([
      new FormGroup({
        deviceSerial: new FormControl('', [
          Validators.required,
          deviceSerialValidator(['inverter', 'meter']),
          serialUniqueValidator,
        ]),
        added: new FormControl(false),
        detected: new FormControl(false),
        deviceType: new FormControl('inverter'),
        online: new FormControl(false),
        dismiss: new FormControl(null),
      }),
    ]),
  });

  get isVirtualDevicesConfigInvalid() {
    return this.virtualDevicesConfig.controls.inverterList.length <= 1;
  }

  get isAzzurroHubDevicesConfigInvalid() {
    const devices = this.azzurroHubDevicesConfig.controls.deviceList as FormArray;
    const addedDevices = devices.controls.filter(
      control => control.get('added')?.value === true
    );
    return addedDevices.length === 0;
  }

  constructor(
    private readonly deleteDeviceInCertifiedPlantMutation: DeleteDeviceInCertifiedPlantMutation,
    private readonly dialog: MatDialog,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly stateService: StateService,
    private readonly uiPromiseService: UiPromiseService,
    private readonly updateDeviceInCertifiedPlantMutation: UpdateDeviceInCertifiedPlantMutation,
    readonly store: Store<SystemsState>,
  ) {
    this.updateAwsPlant = this.updateAwsPlant.bind(this);
    this.updatePlant = this.updatePlant.bind(this);
  }

  openAddDeviceDialog(system: SystemDefinition): void {
    if (!this.virtualDevices || !this.connextDevices || !this.azzurroHubDevices) {
      return;
    }

    const dialogConfig: MatDialogConfig<any> = {
      autoFocus: 'dialog',
      width: '70vh',
      maxHeight: '80vh',
    };

    const withConnext = system.devices?.find(device => {
      const product = new ProductModelService(device.id);
      return product.type?.name === 'connext';
    })?.id || system.aws?.ems?.find(device => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'connext';
    })?.sn;

    const withAzzurroHub = system.configuration?.azzurroHubId ||
                          system.monitoringType === 'AzzurroHub';

    if (withConnext && system.monitoringType === 'Connext') {
      this.prepareConnextDevicesConfig(system);
      this.dialog.open(this.connextDevices, { data: {
        connextDevicesConfig: this.connextDevicesConfig,
        connextSerial: withConnext as string,
        is3PhasePlant: system.data?.config?.pType === 1,
      }, ...dialogConfig });
    } else if (withAzzurroHub) {
      this.prepareAzzurroHubDevicesConfig(system);

      // Prepare existing devices for the component
      const existingDevices = system.devices || [];
      const verifiedDevices = system.metadata?.verifiedDevices || [];
      const verifiedUninstallations = system.metadata?.verifiedUninstallations || [];

      // Combine and deduplicate devices by serial number
      const allKnownDevices = [...existingDevices, ...verifiedDevices, ...verifiedUninstallations];
      const uniqueDevices = allKnownDevices.filter((device, index, array) => {
        const serial = (device as any).id || (device as any).sn;
        return array.findIndex(d => {
          const dSerial = (d as any).id || (d as any).sn;
          return dSerial === serial;
        }) === index;
      });

      console.log('🎯 openAddDeviceDialog - preparing existing devices for Azzurro Hub');
      console.log('🔍 Deduplication: allKnownDevices had', allKnownDevices.length, 'devices, uniqueDevices has', uniqueDevices.length);

      const existingDevicesWithState = uniqueDevices
        .filter(device => {
          const serial = (device as any).id || (device as any).sn;
          const product = new ProductModelService(serial);
          const compatibleTypes = ['inverter', 'meter'];
          const isCompatible = compatibleTypes.includes(product.type?.name || '');
          console.log(`  📱 Device ${serial}: type=${product.type?.name}, compatible=${isCompatible}`);
          return isCompatible;
        })
        .map(device => {
          const serial = (device as any).id || (device as any).sn;
          const wasUninstalled = verifiedUninstallations.some(ud => ud.sn === serial);
          const deviceWithState = {
            serial: serial,
            dismiss: wasUninstalled ? 'Uninstall' as const : null
          };
          console.log(`  🏷️ Device mapped: ${serial} (dismiss: ${deviceWithState.dismiss})`);
          return deviceWithState;
        });

      console.log('📦 Final existingDevicesWithState to pass to component:', existingDevicesWithState);

      this.dialog.open(this.azzurroHubDevices, { data: {
        azzurroHubDevicesConfig: this.azzurroHubDevicesConfig,
        azzurroHubSerial: system.configuration?.azzurroHubId as string,
        deviceValidators: {
          syncValidators: this.azzurroHubDeviceSyncValidators,
          asyncValidators: [
            this.genericDeviceValidator.checkAvailableDeviceWithCertifiedPlant(system.uid ?? ''),
          ],
        },
        existingDevices: existingDevicesWithState,
        isVerifiedMode: system.source === 'aws', // AWS systems support uninstall/delete
      }, ...dialogConfig });
    } else {
      this.prepareVirtualDevicesConfig(system);
      this.dialog.open(this.virtualDevices, { data: {
        virtualDevicesConfig: this.virtualDevicesConfig,
      }, ...dialogConfig });
    }
  }

  prepareVirtualDevicesConfig(system: SystemDefinition): void {
    const devices = (system.devices || []).map((device) => ({ sn: device.id }));
    const verifiedDevices = system.metadata?.verifiedDevices || devices || [];
    const verifiedUninstallations = system.metadata?.verifiedUninstallations || [];

    const allKnownDevices = [...verifiedDevices, ...verifiedUninstallations];

    const inverterArray = this.virtualDevicesConfig.controls.inverterList as FormArray;
    inverterArray.clear();

    for (const device of allKnownDevices) {
      const product = new ProductModelService(device.sn);
      if (VIRTUAL_DEVICE_TYPES.includes(product.type?.name as DeviceType)) {
        const wasUninstalled = verifiedUninstallations.some(ud => ud.sn === device.sn);

        inverterArray.push(new FormGroup({
          inverterSerial: new FormControl(
            device.sn,
            this.inverterSyncValidators,
            this.inverterAsyncValidators(system)
          ),
          added: new FormControl(true),
          dismiss: new FormControl(wasUninstalled ? 'Uninstall' : null),
        }));
      }
    }

    inverterArray.push(new FormGroup({
      inverterSerial: new FormControl(
        '',
        this.inverterSyncValidators,
        this.inverterAsyncValidators(system)
      ),
      added: new FormControl(false),
      dismiss: new FormControl(null),
    }));
  }

  prepareConnextDevicesConfig(system: SystemDefinition): void {
    const connextData = system.data as Partial<ConnextData>;
    const inverterList = connextData?.devices?.filter(device => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'inverter';
    }) as Array<Partial<InverterType>>;

    this.connextDevicesConfig.controls.inverterList.clear();

    for (const inverter of inverterList || []) {
      const inverterSn = inverter.sn as string;
      const inverterModbusId = `${inverter.id || 0}`;
      this.connextDevicesConfig.controls.inverterList.push(new FormGroup({
        inverterSerial: new FormControl(inverterSn, this.connextInverterSyncValidators, this.connextInverterAsyncValidators(system)),
        inverterModbusId: new FormControl(inverterModbusId, [
          Validators.required,
          Validators.min(1),
          Validators.max(255),
          Validators.pattern('^[0-9]*$'),
        ]),
        setup: new FormControl(true),
        registered: new FormControl(true),
      }));
    }

    this.connextDevicesConfig.controls.inverterList.push(new FormGroup({
      inverterSerial: new FormControl('', this.connextInverterSyncValidators, this.connextInverterAsyncValidators(system)),
      inverterModbusId: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(255),
        Validators.pattern('^[0-9]*$'),
      ]),
      setup: new FormControl(false),
      registered: new FormControl(false),
    }));

    const productionSensor = connextData?.devices?.find(device => {
      const product = new ProductModelService(device.sn);
      return product.type?.name === 'no-zcs';
    }) as Partial<InverterType>;

    if (productionSensor) {
      this.connextDevicesConfig.controls.inverterList.clear();
      this.connextDevicesConfig.controls.productionSensor.setValue({
        productionSensorK: (productionSensor.k || 10) / 10,
        id: `${connextData?.info?.sn.sn}`,
        registered: true,
      });
    }
  }

  prepareAzzurroHubDevicesConfig(system: SystemDefinition): void {
    console.log('🔧 prepareAzzurroHubDevicesConfig called for system:', system.uid);

    const deviceArray = this.azzurroHubDevicesConfig.controls.deviceList as FormArray;
    console.log('📋 Form deviceList before clear:', deviceArray.controls.map(c => c.get('deviceSerial')?.value));

    deviceArray.clear();
    console.log('🧹 Form cleared');

    const existingDevices = system.devices || [];
    const verifiedDevices = system.metadata?.verifiedDevices || [];
    const verifiedUninstallations = system.metadata?.verifiedUninstallations || [];

    console.log('📊 Data analysis:');
    console.log('  - existingDevices:', existingDevices.map(d => (d as any).id || (d as any).sn));
    console.log('  - verifiedDevices:', verifiedDevices.map(d => d.sn));
    console.log('  - verifiedUninstallations:', verifiedUninstallations.map(d => d.sn));

    const allKnownDevices = [...existingDevices, ...verifiedDevices, ...verifiedUninstallations];
    console.log('🔗 allKnownDevices:', allKnownDevices.map(d => (d as any).id || (d as any).sn));

    // Don't add existing devices to the form - they will be handled by the component
    // via the [existingDevices] input to avoid duplication
    console.log('⚠️ NOT adding existing devices to form to avoid duplication');

    // Add empty form control for new device input
    deviceArray.push(new FormGroup({
      deviceSerial: new FormControl('', [
        Validators.required,
        deviceSerialValidator(['inverter', 'meter']),
        serialUniqueValidator,
      ]),
      added: new FormControl(false),
      detected: new FormControl(false),
      deviceType: new FormControl('inverter'),
      online: new FormControl(false),
      dismiss: new FormControl(null),
    }));

    console.log('➕ Added empty form control for new device input');
    console.log('📋 Form deviceList after setup:', deviceArray.controls.map(c => ({
      deviceSerial: c.get('deviceSerial')?.value,
      added: c.get('added')?.value
    })));

    // Note: existingDevices will be passed to the component via [existingDevices] input
    // The component will handle initialization of these devices internally
    console.log('✅ prepareAzzurroHubDevicesConfig completed');
  }

  private isAzzurroHubSystem(system: SystemDefinition): boolean {
    return !!(system.configuration?.azzurroHubId || system.monitoringType === 'AzzurroHub');
  }

  updateAzzurroHubDevicesConfig(deviceData: {serial: string, dismiss?: 'Uninstall' | 'Delete' | null}[]): void {
    const deviceArray = this.azzurroHubDevicesConfig.controls.deviceList as FormArray;

    // Clear all existing devices except the empty one for new input
    const emptyControl = deviceArray.controls.find(control =>
      control.get('deviceSerial')?.value === '' && !control.get('added')?.value
    );

    deviceArray.clear();

    // Add all devices from the component
    deviceData.forEach(device => {
      if (device.serial && device.serial.trim() !== '') {
        const product = new ProductModelService(device.serial);
        deviceArray.push(new FormGroup({
          deviceSerial: new FormControl(device.serial, this.azzurroHubDeviceSyncValidators),
          added: new FormControl(true),
          detected: new FormControl(true),
          deviceType: new FormControl(product.type?.name || 'inverter'),
          online: new FormControl(true),
          dismiss: new FormControl(device.dismiss),
        }));
      }
    });

    // Re-add the empty control for new device input
    if (emptyControl) {
      deviceArray.push(emptyControl);
    } else {
      deviceArray.push(new FormGroup({
        deviceSerial: new FormControl('', [
          Validators.required,
          deviceSerialValidator(['inverter', 'meter']),
          serialUniqueValidator,
        ]),
        added: new FormControl(false),
        detected: new FormControl(false),
        deviceType: new FormControl('inverter'),
        online: new FormControl(false),
        dismiss: new FormControl(null),
      }));
    }
  }

  updatePlant(system: SystemDefinition) {
    return system.source === 'aws'
      ? this.updateAwsPlant(system)
      : this.updateFirebasePlant(system);
  }

  updateFirebasePlant(system: SystemDefinition) {
    const currentSystem = this.stateService.getCurrentSystem(system.uid ?? '');
    if (!currentSystem) {
      return;
    }

    const devices = this.virtualDevicesConfig.controls.inverterList.value
      .filter((inverter: any) => inverter.added)
      .map((inverter: any) => inverter.inverterSerial)
      .map((id: string) => {
        const product = new ProductModelService(id);
        const type = product.type?.name as DeviceType;
        return {
          id,
          name: id,
          type,
        };
      });

    const updatedSystem: System = {
      ...currentSystem,
      devices,
    };

    this.uiPromiseService.run(() => this.stateService.saveCurrentSystem(updatedSystem), {
        loading: true,
        loadingMessage: this.labels.updatingSystemConfiguration,
      },
    );
  }

  updateAwsPlant(system: SystemDefinition) {
    this.uiPromiseService.run(async () => {
      if (!system.uid) {
        return;
      }

      const verifiedDevices = system.metadata?.verifiedDevices || [];
      const verifiedUninstallations = system.metadata?.verifiedUninstallations || [];

      const previouslyKnownDevices = [...verifiedDevices, ...verifiedUninstallations];

      const query = gql`
        fragment certifiedplanttype on CertifiedPlantType {
          success
        }
      `;

      let formDevices: PlantDeviceInput[];
      let devicesToDelete: PlantDeviceInput[] = [];
      let devicesToUninstall: PlantDeviceInput[] = [];

      if (this.isAzzurroHubSystem(system)) {
        // For Azzurro Hub, get devices from the form (populated by updateAzzurroHubDevicesConfig)
        const formAzzurroHubDevices = this.azzurroHubDevicesConfig.value.deviceList as {
          deviceSerial: string;
          added: boolean;
          dismiss: RemoveInverterFromPlant | null;
        }[];

        // Only include devices that have been added (not empty input controls)
        const validDevices = formAzzurroHubDevices.filter(device =>
          device.deviceSerial && device.deviceSerial.trim() !== '' && device.added
        );

        formDevices = validDevices.map((device) => {
          const id = device.deviceSerial;
          const product = new ProductModelService(id);
          const type = convertProductTypeNameToPlantDeviceType(product.type?.name as DeviceType);
          return {
            sn: id,
            type,
          } as PlantDeviceInput;
        });

        devicesToDelete = validDevices
          .filter((dev) => dev.dismiss === 'Delete')
          .map((dev) => formDevices.find((fd) => fd.sn === dev.deviceSerial))
          .filter((fd): fd is PlantDeviceInput =>
            !!fd && previouslyKnownDevices.some((d) => d.sn === fd.sn)
          );

        devicesToUninstall = validDevices
          .filter((dev) => dev.dismiss === 'Uninstall')
          .map((dev) => formDevices.find((fd) => fd.sn === dev.deviceSerial))
          .filter((fd): fd is PlantDeviceInput =>
            !!fd && verifiedDevices.some((d) => d.sn === fd.sn)
          );
      } else {
        const formInverterDevices = this.virtualDevicesConfig.value.inverterList as {
          inverterSerial: string;
          added: boolean;
          dismiss: RemoveInverterFromPlant | null;
        }[];

        formDevices = formInverterDevices.map((inverter) => {
          const id = inverter.inverterSerial;
          const product = new ProductModelService(id);
          const type = convertProductTypeNameToPlantDeviceType(product.type?.name as DeviceType);
          return {
            sn: id,
            type,
          } as PlantDeviceInput;
        });

        devicesToDelete = formInverterDevices
          .filter((inv) => inv.dismiss === 'Delete')
          .map((inv) => formDevices.find((fd) => fd.sn === inv.inverterSerial))
          .filter((fd): fd is PlantDeviceInput =>
            !!fd && previouslyKnownDevices.some((d) => d.sn === fd.sn)
          );

        devicesToUninstall = formInverterDevices
          .filter((inv) => inv.dismiss === 'Uninstall')
          .map((inv) => formDevices.find((fd) => fd.sn === inv.inverterSerial))
          .filter((fd): fd is PlantDeviceInput =>
            !!fd && verifiedDevices.some((d) => d.sn === fd.sn)
          );
      }

      const devicesToInstall = formDevices
        .filter((device) => {
          const isAdded = this.isAzzurroHubSystem(system) ?
            this.azzurroHubDevicesConfig.value?.deviceList?.find((d: any) => d.deviceSerial === device.sn)?.added :
            this.virtualDevicesConfig.value?.inverterList?.find((d: any) => d.inverterSerial === device.sn)?.added;
          const dismiss = this.isAzzurroHubSystem(system) ?
            this.azzurroHubDevicesConfig.value?.deviceList?.find((d: any) => d.deviceSerial === device.sn)?.dismiss :
            this.virtualDevicesConfig.value?.inverterList?.find((d: any) => d.inverterSerial === device.sn)?.dismiss;
          return isAdded === true && dismiss === null;
        })
        .filter((fd): fd is PlantDeviceInput =>
          !!fd &&
          !verifiedDevices.some((d) => d.sn === fd.sn) &&
          !verifiedUninstallations.some((d) => d.sn === fd.sn)
        );

      const devicesToRestore = formDevices
        .filter((device) => {
          const isAdded = this.isAzzurroHubSystem(system) ?
            this.azzurroHubDevicesConfig.value?.deviceList?.find((d: any) => d.deviceSerial === device.sn)?.added :
            this.virtualDevicesConfig.value?.inverterList?.find((d: any) => d.inverterSerial === device.sn)?.added;
          const dismiss = this.isAzzurroHubSystem(system) ?
            this.azzurroHubDevicesConfig.value?.deviceList?.find((d: any) => d.deviceSerial === device.sn)?.dismiss :
            this.virtualDevicesConfig.value?.inverterList?.find((d: any) => d.inverterSerial === device.sn)?.dismiss;
          return isAdded === true && dismiss === null;
        })
        .filter((fd): fd is PlantDeviceInput =>
          !!fd && verifiedUninstallations.some((d) => d.sn === fd.sn)
        );

      for (const device of devicesToRestore) {
        await this.updateDeviceInCertifiedPlantMutation.execute(query, {
          plantId: system.uid,
          device: {
            ...device,
            disinstallationTime: 0,
            installationTime: new Date().getTime(),
          },
        });
      }

      for (const device of devicesToDelete) {
        await this.deleteDeviceInCertifiedPlantMutation.execute(query, {
          plantId: system.uid,
          deviceId: device.sn,
        });
      }

      for (const device of devicesToUninstall) {
        const deviceInput: PlantDeviceInput = {
          ...device,
          installationTime: 0,
          disinstallationTime: new Date().getTime(),
        };
        await this.updateDeviceInCertifiedPlantMutation.execute(query, {
          plantId: system.uid,
          device: deviceInput,
        });
      }

      for (const device of devicesToInstall) {
        const deviceInput: PlantDeviceInput = {
          ...device,
          installationTime: new Date().getTime(),
        };
        await this.updateDeviceInCertifiedPlantMutation.execute(query, {
          plantId: system.uid,
          device: deviceInput,
        });
      }

    }, {
      loading: true,
      loadingMessage: this.labels.updatingSystemConfiguration,
    });
  }
}
